import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AppPhoneScreen } from '@/components/AppPhoneTrio'
import { Footer } from '@/components/Footer'
import { Header } from '@/components/Header'
import { getPage } from '@/content/seoPages.mjs'
import { applyPageMeta } from '@/lib/seo'
import NotFound from '@/pages/NotFound'

const ctaClass =
  'inline-flex min-h-[52px] items-center justify-center rounded-full px-8 text-base font-bold tracking-[-0.01em] text-black shadow-[0_14px_36px_-10px_rgba(113,205,217,0.65),inset_0_1px_0_rgba(255,255,255,0.45)] transition-transform duration-200 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] focus-visible:ring-offset-2 focus-visible:ring-offset-black'

const ctaStyle = {
  background: 'linear-gradient(180deg, #8fe0ec 0%, #71cdd9 60%, #5dbac6 100%)',
}

function Shot({ shot, hero = false }) {
  if (shot.frame === 'board') {
    return (
      <ul className="grid grid-cols-2 overflow-hidden rounded-[28px] border border-white/10 bg-white/10">
        {shot.items.map((item) => (
          <li
            key={item}
            className="bg-[#07080c] px-4 py-3.5 text-base font-semibold leading-tight tracking-[-0.03em] sm:px-5 sm:py-4 sm:text-lg border-b border-r border-white/10 [&:nth-child(2n)]:border-r-0 [&:nth-last-child(-n+2)]:border-b-0"
          >
            {item}
          </li>
        ))}
      </ul>
    )
  }

  if (shot.frame === 'phone') {
    return (
      <figure className={`mx-auto w-full ${hero ? 'max-w-[180px] sm:max-w-[200px]' : 'max-w-[150px] sm:max-w-[170px]'}`}>
        <AppPhoneScreen src={shot.src} alt={shot.alt} featured={hero} priority={hero} />
      </figure>
    )
  }

  return (
    <figure className="w-full rounded-[24px] bg-[#eceae4] p-3 sm:p-4 shadow-[0_36px_80px_-28px_rgba(0,0,0,0.9)]">
      <img
        src={shot.src}
        alt={shot.alt}
        width={2880}
        height={1636}
        loading={hero ? 'eager' : 'lazy'}
        decoding="async"
        className="h-auto max-h-[200px] w-full rounded-[16px] object-cover object-left-top sm:max-h-[260px]"
      />
    </figure>
  )
}

function Caption({ shot }) {
  if (!shot?.heading && !shot?.body) return null
  return (
    <div className="max-w-[36rem]">
      {shot.heading ? (
        <h2 className="text-[1.45rem] font-extrabold leading-[1.1] tracking-[-0.03em] text-balance sm:text-[1.7rem]">
          {shot.heading}
        </h2>
      ) : null}
      {shot.body ? <p className="mt-2 text-base leading-relaxed text-white/75">{shot.body}</p> : null}
    </div>
  )
}

function Cta({ page, className = '' }) {
  return (
    <Link to={page.cta.href} className={`${ctaClass} ${className}`} style={ctaStyle}>
      {page.cta.label}
    </Link>
  )
}

export default function SeoLanding({ section }) {
  const params = useParams()
  const slug = section === 'sports' ? params.sport : params.audience
  const page = getPage(`/${section}/${slug}`)

  useEffect(() => {
    if (!page) return undefined
    applyPageMeta({
      title: page.title,
      description: page.description,
      path: page.path,
      ogTitle: page.ogTitle,
      ogDescription: page.ogDescription,
    })
    document.getElementById('seo-faq')?.remove()
    return undefined
  }, [page])

  if (!page) return <NotFound />

  const shots = page.shots || []
  const [heroShot, ...rest] = shots
  const heroWide = heroShot?.frame !== 'phone'
  const beats = page.beats || []
  const marks = page.marks || []

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main>
        <section
          className={`mx-auto grid w-full max-w-[1240px] items-center gap-6 px-5 pt-24 pb-8 sm:px-8 sm:pt-28 lg:gap-10 lg:pb-10 ${
            heroWide
              ? 'md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]'
              : 'md:grid-cols-[minmax(0,1fr)_minmax(200px,240px)]'
          }`}
        >
          <div>
            <h1 className="text-[2.15rem] font-extrabold leading-[1.05] tracking-[-0.04em] text-balance sm:text-[2.6rem] lg:text-[3rem]">
              {page.kicker ? (
                <span className="mb-3 block text-[11px] font-semibold uppercase leading-none tracking-[0.22em] text-[#71cdd9]">
                  {page.kicker}
                </span>
              ) : null}
              {page.h1}
            </h1>
            <p className="mt-3 max-w-[34rem] text-base leading-relaxed text-white/75">{page.lede}</p>
            <Cta page={page} className="mt-5 hidden md:inline-flex" />
          </div>
          {heroShot ? (
            <div>
              <Shot shot={heroShot} hero />
              {heroShot.heading || heroShot.body ? (
                <div className="mt-6">
                  <Caption shot={heroShot} />
                </div>
              ) : null}
            </div>
          ) : null}
          <Cta page={page} className="md:hidden" />
        </section>

        {marks.length ? (
          <section className="border-t border-white/10 bg-[#07080c]">
            <div className="mx-auto w-full max-w-[1240px] px-5 py-8 sm:px-8 lg:py-10">
              <div className="max-w-[40rem]">
                <h2 className="text-[1.45rem] font-extrabold leading-[1.1] tracking-[-0.03em] text-balance sm:text-[1.7rem]">
                  {page.marksTitle}
                </h2>
                {page.marksBody ? (
                  <p className="mt-2 text-base leading-relaxed text-white/75">{page.marksBody}</p>
                ) : null}
              </div>
              <ul className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-4">
                {marks.map((item) => (
                  <li
                    key={item}
                    className="bg-black px-4 py-3 text-sm font-semibold leading-tight tracking-[-0.02em] sm:text-base"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ) : null}

        {beats.length ? (
          <section className="border-t border-white/10">
            <div className="mx-auto grid w-full max-w-[1240px] gap-6 px-5 py-8 sm:px-8 md:grid-cols-3 lg:py-10">
              {beats.map((beat) => (
                <div key={beat.title}>
                  <h2 className="text-lg font-extrabold leading-tight tracking-[-0.03em] sm:text-xl">
                    {beat.title}
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/75 sm:text-base">{beat.body}</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {rest.map((shot) => {
          const phone = shot.frame === 'phone'
          return (
            <section key={shot.src || shot.heading} className="border-t border-white/10">
              <div
                className={`mx-auto grid w-full items-center gap-6 px-5 py-8 sm:px-8 lg:gap-10 lg:py-10 ${
                  phone ? 'max-w-[1100px] md:grid-cols-[180px_minmax(0,1fr)]' : 'max-w-[1240px] md:grid-cols-2'
                }`}
              >
                <Shot shot={shot} />
                <Caption shot={shot} />
              </div>
            </section>
          )
        })}

        {page.closer ? (
          <section className="border-t border-white/10">
            <p className="mx-auto w-full max-w-[720px] px-5 py-8 text-base leading-relaxed text-white/75 sm:px-8 lg:py-10">
              {page.closer}
            </p>
          </section>
        ) : null}

        {page.related?.length ? (
          <nav aria-label="More from Statbox" className="border-t border-white/10">
            <div className="mx-auto grid w-full max-w-[1240px] gap-2 px-5 py-6 sm:grid-cols-3 sm:px-8">
              {page.related.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex min-h-12 items-center rounded-xl border border-white/10 px-4 text-base font-semibold tracking-[-0.02em] text-white/85 hover:border-white/30 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
        ) : null}
      </main>
      <Footer />
    </div>
  )
}
